import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { scrollTo } from "@/components/Nav";
import ProductCard from "@/components/ProductCard";

const WA_URL =
  "https://wa.me/971568498289?text=" +
  encodeURIComponent("Hello Cambroo! I'd like to ask about product availability.");

export default function Featured() {
  const [tab, setTab] = useState("all");
  const [subFilter, setSubFilter] = useState(null);
  const [visibleCount, setVisibleCount] = useState(30);
  const { t, isAr } = useLanguage();
  const { products, categories } = useCart();
  const TABS = [{ id: "all" }, ...categories];

  const activeCat = categories.find((c) => c.id === tab);
  const filtered =
    tab === "all"
      ? products
      : products.filter((p) => p.category === tab && (!subFilter || p.subcategory === subFilter));
  const visible = filtered.slice(0, visibleCount);

  useEffect(() => {
    const onFilter = (e) => {
      const d = e.detail;
      setVisibleCount(30);
      if (typeof d === "string") {
        setTab(d);
        setSubFilter(null);
      } else {
        setTab(d?.category || "all");
        setSubFilter(d?.subcategory || null);
      }
    };
    window.addEventListener("cambroo:filter", onFilter);
    return () => window.removeEventListener("cambroo:filter", onFilter);
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem("cambroo:pending-filter");
    if (raw) {
      sessionStorage.removeItem("cambroo:pending-filter");
      try {
        const d = JSON.parse(raw);
        setTab(d.category || "all");
        setSubFilter(d.subcategory || null);
        setVisibleCount(30);
        setTimeout(() => scrollTo("#products"), 150);
      } catch (e) {
        /* ignore malformed pending filter */
      }
    }
  }, []);

  return (
    <section id="products" data-testid="products-section" className="px-4 py-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-brandink">
            {activeCat ? (isAr ? activeCat.name_ar : activeCat.name) : t("prod_title")}
          </h2>
        </div>

        <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="product-filter-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`filter-tab-${tb.id}`}
              onClick={() => { setTab(tb.id); setSubFilter(null); setVisibleCount(30); }}
              className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                tab === tb.id
                  ? "border-brandink bg-brandink text-white"
                  : "border-line bg-white text-brandink/70 hover:border-brandink/40"
              }`}
            >
              {tb.id === "all" ? t("tab_all") : isAr ? tb.name_ar : tb.name}
            </button>
          ))}
        </div>

        {activeCat?.subs?.length > 0 && (
          <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="subcategory-chips">
            <button
              data-testid="sub-tab-all"
              onClick={() => { setSubFilter(null); setVisibleCount(30); }}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1 text-[11px] font-semibold transition-colors duration-200 ${
                !subFilter
                  ? "border-brand bg-[#FFFBE0] text-brandink"
                  : "border-line bg-white text-muted hover:border-brandink/40"
              }`}
            >
              {t("tab_all")}
            </button>
            {activeCat.subs.map((s) => (
              <button
                key={s.id}
                data-testid={`sub-tab-${s.id}`}
                onClick={() => { setSubFilter(s.id); setVisibleCount(30); }}
                className={`whitespace-nowrap rounded-full border px-3.5 py-1 text-[11px] font-semibold transition-colors duration-200 ${
                  subFilter === s.id
                    ? "border-brand bg-[#FFFBE0] text-brandink"
                    : "border-line bg-white text-muted hover:border-brandink/40"
                }`}
              >
                {isAr ? s.name_ar : s.name}
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div
            data-testid="products-empty"
            className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-line bg-white p-12 text-center"
          >
            <p className="text-base font-bold text-brandink">{t("coming_soon_title")}</p>
            <p className="max-w-sm text-sm text-muted">{t("coming_soon_sub")}</p>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="products-empty-whatsapp"
              className="mt-2 flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-xs font-bold text-brandink transition-colors hover:bg-branddark"
            >
              <MessageCircle className="h-4 w-4" />
              {t("wa_tooltip")}
            </a>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <AnimatePresence mode="popLayout">
              {visible.map((p) => (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="h-full"
                >
                  <ProductCard product={p} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {filtered.length > visible.length && (
          <div className="mt-6 text-center">
            <button
              data-testid="load-more-button"
              onClick={() => setVisibleCount((c) => c + 30)}
              className="rounded-md border border-line bg-white px-8 py-2.5 text-xs font-bold text-brandink transition-colors hover:border-brandink"
            >
              {t("load_more")} ({filtered.length - visible.length})
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
